import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Session {
  id: string;
  label: string | null;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

function ago(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 2) return '刚刚';
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

/**
 * 登录设备。
 *
 * ★ 允许多端登录之后,这一屏就是**防账号转借的那条防线**。
 *
 *   原来是「一账号一设备」:新设备一登录,旧的直接被踢 ——
 *   借出去自己就用不了,这是最土也最有效的拦截。
 *   但它同时把「手机 + 平板换着用」也拦掉了,而被踢的那端
 *   只看到莫名其妙的退出登录。
 *
 *   硬拦截换成看得见:自己账号上多出一台不认识的设备,当场踢掉。
 *   看不见的话,借出去的账号可以用一整年都没人发现。
 */
export function DeviceList() {
  const [rows, setRows] = useState<Session[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ data: Session[] }>('/auth/sessions');
      setRows(r.data);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(id: string) {
    setBusy(id);
    try {
      await api(`/auth/sessions/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '踢下线失败');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      aria-label="登录设备"
      className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700
                 dark:bg-slate-800"
    >
      <div className="text-slate-900 dark:text-slate-100">登录设备</div>
      <p className="mt-1 text-xs text-slate-400">
        同一个账号可以在几台设备上一起用。看到不认识的,直接踢下线并改密码
      </p>

      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}

      <ul className="mt-3 space-y-2">
        {rows.map((s) => (
          <li key={s.id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-slate-800 dark:text-slate-200">
                {s.label ?? '未命名设备'}
                {/* ★ 标出「你正在用的这台」—— 不标的话没人敢踢任何一台 */}
                {s.current && (
                  <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700
                                   dark:bg-brand-500/10 dark:text-brand-400">
                    这一台
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400">最近活跃 {ago(s.lastSeenAt)}</div>
            </div>
            {!s.current && (
              <button
                onClick={() => void revoke(s.id)}
                disabled={busy === s.id}
                className="shrink-0 rounded-lg border border-slate-300 px-2 py-1 text-xs
                           text-slate-600 disabled:opacity-40 dark:border-slate-600
                           dark:text-slate-300"
              >
                {busy === s.id ? '…' : '踢下线'}
              </button>
            )}
          </li>
        ))}
        {rows.length === 0 && <li className="text-sm text-slate-400">只有当前这一台</li>}
      </ul>
    </section>
  );
}
